import type {
  AvailableLocale,
  LocaleMessages,
} from '../LanguageProvider/shared';
import './polyfills';

export const DEFAULT_LOCALE: AvailableLocale = 'en-US';

/**
 * Compiled file per locale. Smartling names the files by region code, which is
 * not the key the app uses for the locale (`de` → `de-DE.json`), so the mapping
 * cannot be derived from the locale alone.
 */
const TRANSLATION_FILE: Record<AvailableLocale, string> = {
  ar: 'ar',
  be: 'be-BY',
  bg: 'bg-BG',
  bs: 'bs-BA',
  cs: 'cs-CZ',
  da: 'da-DK',
  de: 'de-DE',
  el: 'el-GR',
  'en-GB': 'en-GB',
  'en-US': 'en-US',
  es: 'es-ES',
  fi: 'fi-FI',
  fr: 'fr-FR',
  'fr-CA': 'fr-CA',
  he: 'he-IL',
  'hi-IN': 'hi-IN',
  hr: 'hr-HR',
  ht: 'ht-HT',
  hu: 'hu-HU',
  is: 'is-IS',
  it: 'it-IT',
  ja: 'ja-JP',
  ka: 'ka-GE',
  lb: 'lb',
  mk: 'mk-MK',
  ml: 'ml-IN',
  nl: 'nl-NL',
  no: 'no-NO',
  pl: 'pl-PL',
  pt: 'pt-BR',
  ro: 'ro-RO',
  ru: 'ru-RU',
  sk: 'sk-SK',
  sl: 'sl-SI',
  sv: 'sv-SE',
  tl: 'tl-PH',
  uk: 'uk-UA',
};

async function importCompiled(file: string): Promise<LocaleMessages> {
  const compiled = await import(`../translations/compiled/${file}.json`);
  return (compiled.default ?? compiled) as LocaleMessages;
}

/**
 * One locale's compiled messages. The dynamic specifier is load-bearing: the
 * bundler emits a chunk per locale, so a page carries only the language it
 * renders. A static map would put all 37 in the bundle of every layout that
 * imports it.
 *
 * Callers resolve this on the server and hand the result to
 * {@link LanguageProvider}, so the first render already has its copy. The
 * authenticated layout awaits it, so a locale whose file fails to load gets
 * the en-US set rather than an error page.
 */
export async function loadTranslationMessages(
  locale: AvailableLocale
): Promise<LocaleMessages> {
  const fallbackFile = TRANSLATION_FILE[DEFAULT_LOCALE];
  const file = TRANSLATION_FILE[locale] ?? fallbackFile;
  try {
    return await importCompiled(file);
  } catch (error) {
    if (file === fallbackFile) {
      throw error;
    }
    console.error(
      `[i18n] Failed to load messages for ${locale}; using ${DEFAULT_LOCALE}`,
      error
    );
    return importCompiled(fallbackFile);
  }
}
